/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import { useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { IGeoRegion, INotificationGrpData } from "../../../../store/api/admin/types";
import { setAddDeviceGeoRegion, setAddDeviceSiteGroup, setGeoRegion, setSiteGroup } from "../../../../store/filters/devices/NotificationDeviceFilterReducer";
import { useStoreDispatch } from "../../../../store/hooks";
import { addDeviceSiteGroupSelector, addDeviceGeoRegionSelector, geoRegionSelector, groupKeySelector, siteGroupSelector } from "../../../../store/filters/devices/NotificationDeviceFilterSelectors";
import { IFilterSelectBox } from "../../../Shared/Filter/types";
import { useRegionDisplay } from "../../../../pages/Administrations/AccessControls/Regions/hooks";
import { AppState } from "../../../../store";
import { getFilter } from "../../../Shared/Association/AssociationHelper";


export const useDevices = () => {
    const dispatch = useStoreDispatch();
    const notificationDeviceGroup = useSelector(groupKeySelector);
    const siteGroup = useSelector(siteGroupSelector);
    const selectedGeoRegion = useSelector<AppState, IGeoRegion>(geoRegionSelector);
    const [group, setGroup] = useState<INotificationGrpData>(notificationDeviceGroup);
    const { geoRegions } = useRegionDisplay();
    const regionOptions = geoRegions.map((region) => ({ value: region.objectKey, text: region.name }));

    const siteGroups = selectedGeoRegion.objectKey
        ? geoRegions.find((region: IGeoRegion) => region.objectKey === selectedGeoRegion.objectKey)?.siteGroups || []
        : geoRegions.flatMap((region: IGeoRegion) => region.siteGroups || []);
    const siteGroupOptions = Array.from(new Set(siteGroups.map((siteGroup: any) => siteGroup.name)))
        .map((name) => ({ value: name, text: name }));

    const prevGeoRegion = useRef(selectedGeoRegion.objectKey);
    useEffect(() => {
        if (prevGeoRegion.current !== selectedGeoRegion.objectKey) {
            dispatch(setSiteGroup(""));
            prevGeoRegion.current = selectedGeoRegion.objectKey;
        }
    }, [selectedGeoRegion.objectKey, dispatch]);

    const searchFilters: IFilterSelectBox[] = [
        getFilter(
            "geoRegion",
            "All Regions",
            selectedGeoRegion.objectKey,
            setGeoRegion,
            regionOptions,
            "123px"
        ),
        getFilter(
            "group",
            "All Groups",
            siteGroup || "",
            setSiteGroup,
            siteGroupOptions,
            "119px"
        ),
    ];

    // Add 
    const addDeviceSiteGroup = useSelector(addDeviceSiteGroupSelector);
    const selectedAddDeviceGeoRegion = useSelector<AppState, IGeoRegion>(addDeviceGeoRegionSelector);

    const addRegionOptions = geoRegions.map((region) => ({ value: region.objectKey, text: region.name }));

    const addSiteGroups = selectedAddDeviceGeoRegion.objectKey
        ? geoRegions.find((region: IGeoRegion) => region.objectKey === selectedAddDeviceGeoRegion.objectKey)?.siteGroups || []
        : geoRegions.flatMap((region: IGeoRegion) => region.siteGroups || []);

    const addSiteGroupOptions = Array.from(new Set(addSiteGroups.map((siteGroup: any) => siteGroup.name)))
        .map((name) => ({ value: name, text: name }));

    const prevAddGeoRegion = useRef(selectedAddDeviceGeoRegion.objectKey);
    useEffect(() => {
        if (prevAddGeoRegion.current !== selectedAddDeviceGeoRegion.objectKey) {
            dispatch(setAddDeviceSiteGroup(""));
            prevAddGeoRegion.current = selectedAddDeviceGeoRegion.objectKey;
        }
    }, [selectedAddDeviceGeoRegion.objectKey, dispatch]);

    const searchAddFilters: IFilterSelectBox[] = [
        getFilter(
            "addDeviceGeoRegion",
            "All Regions",
            selectedAddDeviceGeoRegion.objectKey,
            setAddDeviceGeoRegion,
            addRegionOptions,
            "123px"
        ),
        getFilter(
            "addDeviceGroup",
            "All Groups",
            addDeviceSiteGroup || "",
            setAddDeviceSiteGroup,
            addSiteGroupOptions,
            "119px"
        ),
    ];

    useEffect(() => { setGroup(notificationDeviceGroup) },
        [notificationDeviceGroup]);

    return {
        searchFilters,
        searchAddFilters,
        group
    };
}